import { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitGroupJoin } from "@/lib/api";
import { PARISH } from "@/lib/constants";

const JoinModal = ({ open, onClose, groupName }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [hp, setHp] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ name: "", email: "", phone: "", message: "" });
            setHp("");
        }
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please enter your name and email.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await submitGroupJoin({ group: groupName, group_name: groupName, ...form, hp });
            const subject = encodeURIComponent(`Request to join ${groupName}`);
            const body = encodeURIComponent(
                `Hello Holy Trinity Kileleshwa,\n\nI would like to join ${groupName}.\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "-"}\n\n${form.message || ""}\n\nGod bless.`
            );
            toast.success("Your request has been received. Welcome!", {
                description: `Sent to the Parish Office. You can also reach us directly at ${PARISH.emails.info}.`,
                action: {
                    label: "Email parish too",
                    onClick: () => {
                        window.location.href = `mailto:${PARISH.emails.info}?subject=${subject}&body=${body}`;
                    },
                },
                duration: 9000,
            });
            onClose();
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error(err.response.data?.detail || "Too many submissions. Please try again later.");
            } else {
                toast.error(`Couldn't send your request. Please email us directly at ${PARISH.emails.info}.`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-maroon-900/60 backdrop-blur-sm px-4 py-6 animate-fade-up"
            onClick={onClose}
            data-testid="join-modal"
        >
            <div
                className="w-full max-w-lg rounded-2xl bg-cream-50 shadow-xl border border-maroon-100"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 p-5 border-b border-maroon-100">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500 font-semibold">Join</div>
                        <h3 className="font-serif text-2xl text-maroon-800 leading-tight mt-0.5">{groupName}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-full p-1.5 text-maroon-700 hover:bg-maroon-100"
                        aria-label="Close"
                        data-testid="join-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider text-maroon-700/80">Full Name</span>
                            <input
                                data-testid="join-name"
                                value={form.name}
                                onChange={handleChange("name")}
                                required
                                maxLength={120}
                                className="mt-1 w-full rounded-xl border border-maroon-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider text-maroon-700/80">Email</span>
                            <input
                                data-testid="join-email"
                                type="email"
                                value={form.email}
                                onChange={handleChange("email")}
                                required
                                maxLength={160}
                                className="mt-1 w-full rounded-xl border border-maroon-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs uppercase tracking-wider text-maroon-700/80">Phone (optional)</span>
                        <input
                            data-testid="join-phone"
                            value={form.phone}
                            onChange={handleChange("phone")}
                            maxLength={40}
                            className="mt-1 w-full rounded-xl border border-maroon-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-wider text-maroon-700/80">A short message (optional)</span>
                        <textarea
                            data-testid="join-message"
                            value={form.message}
                            onChange={handleChange("message")}
                            rows={3}
                            maxLength={800}
                            placeholder="Tell us a bit about yourself or any questions."
                            className="mt-1 w-full rounded-xl border border-maroon-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 resize-none"
                        />
                    </label>
                    {/* Honeypot */}
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <p className="text-[11px] text-maroon-700/70 italic">
                        Your request will be sent to the Parish Office. You can also reach us directly at{" "}
                        <a href={`mailto:${PARISH.emails.info}`} className="underline decoration-gold-400 underline-offset-4 hover:text-maroon-900">
                            {PARISH.emails.info}
                        </a>.
                    </p>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="join-submit"
                        className="btn-primary w-full justify-center disabled:opacity-60"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send my request
                    </button>
                </form>
            </div>
        </div>
    );
};

export default JoinModal;
